import React, { useState } from 'react';
import { 
  Button, 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Alert,
  Divider
} from '@mui/material';
import { refreshToken, getToken, isAuthenticated } from '../../auth/Auth';
import { persistStore } from '../../../persist-store';

/**
 * Component to test token refresh functionality
 */
const TestTokenRefresh: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [tokenInfo, setTokenInfo] = useState<any>(null);

  // Check current token status
  const checkTokenStatus = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get current token
      const token = await getToken();
      const authenticated = await isAuthenticated();
      
      // Check if we have a refresh token in different storage locations
      const cookieRefreshToken = await persistStore.getCookie<string>('refresh_token');
      const sessionRefreshToken = await persistStore.getSession<string>('refresh_token');
      const secureTokens = await persistStore.getSecure<{
        refreshToken: string;
      }>('auth_tokens');
      
      // If we have a token, decode and extract expiration
      let tokenExpiration = null;
      let tokenPayload = null;
      
      if (token) {
        try {
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split('')
              .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join('')
          );
          tokenPayload = JSON.parse(jsonPayload);
          tokenExpiration = tokenPayload.exp ? new Date(tokenPayload.exp * 1000) : null;
        } catch (e) {
          console.error('Error decoding token:', e);
        }
      }
      
      setTokenInfo({
        hasToken: !!token,
        isAuthenticated: authenticated,
        hasRefreshTokenCookie: !!cookieRefreshToken,
        hasRefreshTokenSession: !!sessionRefreshToken,
        hasRefreshTokenSecure: !!secureTokens?.refreshToken,
        tokenExpiration: tokenExpiration ? tokenExpiration.toLocaleString() : 'N/A',
        expiresIn: tokenExpiration ? Math.floor((tokenExpiration.getTime() - Date.now()) / 1000) : 'N/A',
        tokenPayload
      });
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Force token refresh
  const handleForceRefresh = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const refreshed = await refreshToken();
      setResult({
        success: refreshed,
        message: refreshed ? 'Token refreshed successfully' : 'Failed to refresh token',
        timestamp: new Date().toISOString()
      });
      
      // Update token info after refresh
      await checkTokenStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Check token status on component mount
  React.useEffect(() => {
    checkTokenStatus();
  }, []);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button 
          variant="contained" 
          onClick={handleForceRefresh} 
          disabled={loading}
          sx={{ minWidth: '150px' }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Force Token Refresh'}
        </Button>
        
        <Button 
          variant="outlined" 
          onClick={checkTokenStatus} 
          disabled={loading}
        >
          Check Token Status
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {tokenInfo && (
        <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Current Token Status
          </Typography>
          <Typography variant="body1">
            Has Valid Token: {tokenInfo.hasToken ? 'Yes' : 'No'}
          </Typography>
          <Typography variant="body1">
            Is Authenticated: {tokenInfo.isAuthenticated ? 'Yes' : 'No'}
          </Typography>
          <Typography variant="body1">
            Has Refresh Token (Cookie): {tokenInfo.hasRefreshTokenCookie ? 'Yes' : 'No'}
          </Typography>
          <Typography variant="body1">
            Has Refresh Token (Session): {tokenInfo.hasRefreshTokenSession ? 'Yes' : 'No'}
          </Typography>
          <Typography variant="body1">
            Has Refresh Token (Secure): {tokenInfo.hasRefreshTokenSecure ? 'Yes' : 'No'}
          </Typography>
          <Typography variant="body1">
            Token Expires: {tokenInfo.tokenExpiration}
          </Typography>
          <Typography variant="body1">
            Expires In: {typeof tokenInfo.expiresIn === 'number' ? `${tokenInfo.expiresIn} seconds` : tokenInfo.expiresIn}
          </Typography>
          
          {tokenInfo.tokenPayload && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Token Payload
              </Typography>
              <Box sx={{ 
                maxHeight: '200px', 
                overflow: 'auto', 
                bgcolor: 'background.default',
                p: 1,
                borderRadius: 1
              }}>
                <pre style={{ margin: 0 }}>
                  {JSON.stringify(tokenInfo.tokenPayload, null, 2)}
                </pre>
              </Box>
            </>
          )}
        </Paper>
      )}
      
      {result && (
        <Paper 
          elevation={3} 
          sx={{ p: 2, mb: 2, bgcolor: result.success ? 'success.light' : 'error.light' }}
        >
          <Typography variant="h6" gutterBottom>
            Refresh Result
          </Typography>
          <Typography variant="body1" gutterBottom>
            Success: {result.success ? 'Yes' : 'No'}
          </Typography>
          <Typography variant="body1" gutterBottom>
            Message: {result.message}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Timestamp: {result.timestamp}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default TestTokenRefresh;